import { TFunction } from 'i18next';
import {
    ActivityChangedAttributes,
    ActivityChangedLicenseSystemAccess,
    ActivityChangedModuleAccess,
    ActivityChangedSiteAccess,
    ActivityClosedSessions,
    ActivityDetails,
    ActivityDownloadedLicenseSystemUpdate,
    ActivityType,
    ChangedAttribute,
} from 'src/graphql/generated/types';
import { ModuleId, modules } from 'src/graphql/mappers/module';

type ActivityTargetResource = {
    orgId?: string | null;
    orgName?: string | null;
    userName?: string | null;
    userEmail?: string | null;
    licenseSystemId?: string | null;
};

type ActivityInputBase = {
    targetResource: ActivityTargetResource;
};

type ActivityInputWithDetails<TType extends ActivityType, TDetails extends ActivityDetails> = ActivityInputBase & {
    activityType: TType;
    activityDetails?: TDetails | null;
};

type ActivityInputWithoutDetails<TType extends ActivityType> = ActivityInputBase & {
    activityType: TType;
};

type AttributeDetailsActivityType =
    | ActivityType.EditUser
    | ActivityType.ChangeUserName
    | ActivityType.EditOrganization
    | ActivityType.EditLicenseSystem;

type LicenseSystemAccessActivityType =
    | ActivityType.AssignLicenseSystemAccessToUser
    | ActivityType.RemoveLicenseSystemAccessFromUser;

type ModuleAccessActivityType =
    | ActivityType.AssignModulesToUser
    | ActivityType.RemoveModulesFromUser;

type SiteAccessActivityType =
    | ActivityType.AssignSitesToOrganization
    | ActivityType.RemoveSitesFromOrganization;

type ActivityTypeWithoutDetails =
    | ActivityType.InviteUserToOrg
    | ActivityType.DeleteUser
    | ActivityType.ResendInviteToOrg
    | ActivityType.JoinOrganization
    | ActivityType.CreateOrganization
    | ActivityType.AddLicenseSystemToOrg
    | ActivityType.RemoveLicenseSystemFromOrg
    | ActivityType.RequestLicensesTransfer
    | ActivityType.FailLicensesTransfer
    | ActivityType.CompleteLicensesTransfer
    | ActivityType.RequestRepairLicenseSystem
    | ActivityType.RequestTemporaryLicenses
    | ActivityType.RequestCreateLicenseSystem;

export type ActivityMessageInput =
    | ActivityInputWithDetails<AttributeDetailsActivityType, ActivityChangedAttributes>
    | ActivityInputWithDetails<LicenseSystemAccessActivityType, ActivityChangedLicenseSystemAccess>
    | ActivityInputWithDetails<ModuleAccessActivityType, ActivityChangedModuleAccess>
    | ActivityInputWithDetails<SiteAccessActivityType, ActivityChangedSiteAccess>
    | ActivityInputWithDetails<ActivityType.DownloadLicenseUpdate, ActivityDownloadedLicenseSystemUpdate>
    | ActivityInputWithDetails<ActivityType.CloseActiveSessions, ActivityClosedSessions>
    | ActivityInputWithoutDetails<ActivityTypeWithoutDetails>;

export const normalizeActivityMessageInput = ({
    activityType,
    activityDetails,
    targetResource,
}: {
    activityType: ActivityType;
    activityDetails?: ActivityDetails | null;
    targetResource: ActivityTargetResource;
}): ActivityMessageInput | null => {
    switch (activityType) {
        case ActivityType.EditUser:
        case ActivityType.ChangeUserName:
        case ActivityType.EditOrganization:
        case ActivityType.EditLicenseSystem:
            if (activityDetails && activityDetails.__typename !== 'ActivityChangedAttributes') return null;
            return {
                activityType,
                activityDetails,
                targetResource,
            };
        case ActivityType.AssignLicenseSystemAccessToUser:
        case ActivityType.RemoveLicenseSystemAccessFromUser:
            if (activityDetails && activityDetails.__typename !== 'ActivityChangedLicenseSystemAccess') return null;
            return {
                activityType,
                activityDetails,
                targetResource,
            };
        case ActivityType.AssignModulesToUser:
        case ActivityType.RemoveModulesFromUser:
            if (activityDetails && activityDetails.__typename !== 'ActivityChangedModuleAccess') return null;
            return {
                activityType,
                activityDetails,
                targetResource,
            };
        case ActivityType.DownloadLicenseUpdate:
            if (activityDetails && activityDetails.__typename !== 'ActivityDownloadedLicenseSystemUpdate') return null;
            return {
                activityType,
                activityDetails,
                targetResource,
            };
        case ActivityType.InviteUserToOrg:
        case ActivityType.DeleteUser:
        case ActivityType.ResendInviteToOrg:
        case ActivityType.JoinOrganization:
        case ActivityType.CreateOrganization:
        case ActivityType.AddLicenseSystemToOrg:
        case ActivityType.RemoveLicenseSystemFromOrg:
        case ActivityType.RequestRepairLicenseSystem:
        case ActivityType.RequestTemporaryLicenses:
        case ActivityType.RequestCreateLicenseSystem:
        case ActivityType.RequestLicensesTransfer:
        case ActivityType.FailLicensesTransfer:
        case ActivityType.CompleteLicensesTransfer:
            return {
                activityType,
                targetResource,
            };
        case ActivityType.CloseActiveSessions:
            if (activityDetails && activityDetails.__typename !== 'ActivityClosedSessions') return null;
            return {
                activityType,
                activityDetails,
                targetResource,
            };
        case ActivityType.AssignSitesToOrganization:
        case ActivityType.RemoveSitesFromOrganization:
            if (activityDetails && activityDetails.__typename !== 'ActivityChangedSiteAccess') return null;
            return {
                activityType,
                activityDetails,
                targetResource,
            };
        default:
            // We don't want to show unexpected activity types.
            return null;
    }
};

type TranslateOptions = Record<string, string | number>;

const EMPTY_VALUE = '-';
const ACTIVITY_PROTOCOL_MESSAGES_LOCALIZATION_PATH = 'ActivityProtocol.messages';

const addQuotationMarks = (value?: string | null) => (value ? `"${value}"` : '"-"');

const getActivityMessageKey = (suffix: string): string => `${ACTIVITY_PROTOCOL_MESSAGES_LOCALIZATION_PATH}.${suffix}`;

const translateActivityMessage = (
    t: TFunction,
    suffix: string,
    defaultValue: string,
    options?: TranslateOptions,
): string => t(getActivityMessageKey(suffix), defaultValue, options);

const formatTargetUser = (targetResource: ActivityMessageInput['targetResource']) => {
    const name = targetResource.userName || '';
    const email = targetResource.userEmail || EMPTY_VALUE;
    if (!name) return email;
    return `${name} (${email})`;
};

const getChangedAttributes = (activityDetails?: ActivityDetails | null): ChangedAttribute[] => {
    if (activityDetails?.__typename !== 'ActivityChangedAttributes') return [];
    return activityDetails.attributes || [];
};

const getModuleIds = (activityDetails?: ActivityDetails | null): string[] => {
    if (activityDetails?.__typename !== 'ActivityChangedModuleAccess') return [];
    return activityDetails.moduleIds || [];
};

const getLicenseSystemIdsText = (licenseSystemIds: string[] | undefined): string =>
    (licenseSystemIds || []).join(', ');

const getUpdateNumber = (activityDetails?: ActivityDetails | null): number | null => {
    if (activityDetails?.__typename !== 'ActivityDownloadedLicenseSystemUpdate') return null;
    return activityDetails.updateNumber;
};

const CLOSED_SESSION_APP_DISPLAY_NAMES = new Map<string, string>([
    ['Measure', 'GMG Measure'],
    ['PhotoshopPlugin', 'GMG ColorPlugin for Adobe® Photoshop®'],
    ['IllustratorPlugin', 'GMG ColorPlugin for Adobe® Illustrator®'],
]);

const getSiteNamesText = (activityDetails?: ActivityDetails | null): string => {
    if (activityDetails?.__typename !== 'ActivityChangedSiteAccess') return EMPTY_VALUE;
    return (activityDetails.sites || []).map((site) => site.orgName).join(', ') || EMPTY_VALUE;
};

const getHeadOrgName = (activityDetails?: ActivityDetails | null): string | null => {
    if (activityDetails?.__typename !== 'ActivityChangedSiteAccess') return null;
    return activityDetails.headOrg?.orgName ?? null;
};

const getClosedSessionsAppNamesText = (activityDetails?: ActivityDetails | null): string | null => {
    if (activityDetails?.__typename !== 'ActivityClosedSessions') return null;
    return (activityDetails.appNames || []).map((appName) => CLOSED_SESSION_APP_DISPLAY_NAMES.get(appName) || appName).join(', ');
};

const mapModuleIdToName = (moduleId?: string) => {
    if (!moduleId) return 'no module was specified'; // should not happen
    const moduleName = modules.get(moduleId as ModuleId);
    return moduleName || `Unknown module ${moduleId}`;
};

type AttributeLabelConfig = {
    key: string;
    defaultValue: string;
};

const normalizeAttributeKey = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

const ATTRIBUTE_LABELS_RAW = new Map<string, AttributeLabelConfig>([
    // editable user attributes
    ['firstName', { key: 'UpdateUserNameModalContainer.first_name_title', defaultValue: 'First name' }],
    ['lastName', { key: 'UpdateUserNameModalContainer.family_name_title', defaultValue: 'Last name' }],
    ['role', { key: 'AddOrEditUserModal.role_title', defaultValue: 'User role' }],
    // editable organization attributes
    ['name', { key: 'NewOrgModal.input_name_tb', defaultValue: 'Organization name' }],
    ['address', { key: 'NewOrgModal.input_address_tb', defaultValue: 'Street name and number' }],
    ['zip', { key: 'NewOrgModal.input_zip_tb', defaultValue: 'ZIP/postal code' }],
    ['city', { key: 'NewOrgModal.input_city_tb', defaultValue: 'City' }],
    ['country', { key: 'NewOrgModal.input_country_tb', defaultValue: 'Country' }],
    ['contactPerson', { key: 'NewOrgModal.input_contactPerson_tb', defaultValue: 'Primary contact name (optional)' }],
    ['contactEmail', { key: 'NewOrgModal.input_contactEmail_tb', defaultValue: 'Primary contact email address' }],
    ['contactPhone', { key: 'NewOrgModal.input_contactPhone_tb', defaultValue: 'Phone (optional)' }],
    ['isAllowedToUseLicenseSystemsOffline', { key: 'NewOrgModal.enable_offline_licensing_cb', defaultValue: 'Approve offline Licensing (high security)' }],
    // editable license system attributes
    ['location', { key: 'LicenseManagementTable.columns.location_title', defaultValue: 'Location' }],
]);

const ATTRIBUTE_LABELS = new Map<string, AttributeLabelConfig>(
    Array.from(ATTRIBUTE_LABELS_RAW.entries()).map(([key, value]) => [normalizeAttributeKey(key), value]),
);

const ROLE_LABEL_KEYS = new Set<string>([
    'GMGAdmin_label',
    'GMGSuperUser_label',
    'Member_label',
    'OrganizationGroupAdmin_label',
    'OrganizationAdmin_label',
    'undefined_label',
]);

const getRoleLabelKey = (value: string): string | null => {
    if (ROLE_LABEL_KEYS.has(value)) return value;
    const withSuffix = `${value}_label`;
    if (ROLE_LABEL_KEYS.has(withSuffix)) return withSuffix;
    return null;
};

const getLocalizedAttributeValue = (value: string | null | undefined, attributeName: string, t: TFunction): string | null | undefined => {
    if (!value) return value;

    const trimmedValue = value.trim();
    if (!trimmedValue) return trimmedValue;

    const normalizedValue = trimmedValue.toLowerCase();
    if (normalizedValue === 'true') return translateActivityMessage(t, 'valueActivated', 'activated');
    if (normalizedValue === 'false') return translateActivityMessage(t, 'valueDeactivated', 'deactivated');

    const normalizedAttribute = attributeName.trim().toLowerCase();
    if (normalizedAttribute === 'role') {
        const roleLabelKey = getRoleLabelKey(trimmedValue);
        if (roleLabelKey) return t(`Roles.${roleLabelKey}`, { defaultValue: trimmedValue });
    }

    return value;
};

const getLocalizedAttributeName = (attributeName: string, t: TFunction): string => {
    const normalized = normalizeAttributeKey(attributeName);

    const config = ATTRIBUTE_LABELS.get(normalized);
    if (config) return t(config.key, { defaultValue: config.defaultValue });
    return attributeName;
};

const getModuleNamesText = (activityDetails?: ActivityDetails | null): string =>
    getModuleIds(activityDetails)
        .map((moduleId) => modules.get(moduleId as ModuleId) || moduleId)
        .join(', ');

const formatChangedAttributeMessage = ({
    details,
    prefix,
    t,
}: {
    details?: ActivityDetails | null;
    prefix: string;
    t: TFunction;
}) => {
    const attrs = getChangedAttributes(details);
    if (!attrs.length) return prefix;
    if (attrs.length === 1) {
        const attr = attrs[0];
        const attributeName = getLocalizedAttributeName(attr.name, t);
        const singleChange =
            t(
                getActivityMessageKey('attributeChangedSingle'),
                '{{attribute}} from {{oldValue}} to {{newValue}}',
                {
                    attribute: attributeName,
                    oldValue: addQuotationMarks(getLocalizedAttributeValue(attr.oldValue, attr.name, t)),
                    newValue: addQuotationMarks(getLocalizedAttributeValue(attr.newValue, attr.name, t)),
                },
            );
        return `${prefix} ${singleChange}`;
    }

    const changes = attrs
        .map((attr) => translateActivityMessage(
            t,
            'attributeChangedMultipleItem',
            '{{attribute}} to {{newValue}}',
            {
                attribute: getLocalizedAttributeName(attr.name, t),
                newValue: addQuotationMarks(getLocalizedAttributeValue(attr.newValue, attr.name, t)),
            },
        ))
        .join(', ');
    return `${prefix} ${changes}`;
};

export const mapActivityToMessage = (activity: ActivityMessageInput, t: TFunction): string => {
    const formattedTargetUser = formatTargetUser(activity.targetResource);
    const orgName = activity.targetResource.orgName || EMPTY_VALUE;
    const licenseSystemId = activity.targetResource.licenseSystemId || EMPTY_VALUE;

    const updateSettingsPrefix = (resource: string): string =>
        translateActivityMessage(t, 'updateSettingsPrefix', 'Updated settings of {{resource}}:', {
            resource,
        });

    switch (activity.activityType) {
        // Accepted by ux
        case ActivityType.InviteUserToOrg:
            return translateActivityMessage(t, 'inviteUserToOrg', 'Invited {{email}}', {
                email: activity.targetResource.userEmail || EMPTY_VALUE,
            });
        case ActivityType.CloseActiveSessions: {
            const prefix = translateActivityMessage(t, 'closeActiveSessions', 'Closed active sessions for {{user}}', {
                user: formattedTargetUser,
            });
            const appNamesText = getClosedSessionsAppNamesText(activity.activityDetails);
            return appNamesText ? `${prefix} ${appNamesText}` : prefix;
        }// Accepted by ux
        case ActivityType.DeleteUser:
            return translateActivityMessage(t, 'deleteUser', 'Deleted {{user}}', {
                user: formattedTargetUser,
            });
        // Accepted by ux
        case ActivityType.ResendInviteToOrg:
            return translateActivityMessage(t, 'resendInviteToOrg', 'Resent invitation to {{user}}', {
                user: formattedTargetUser,
            });

        // Accepted by ux
        case ActivityType.EditUser:
        case ActivityType.ChangeUserName:
            return formatChangedAttributeMessage({
                details: activity.activityDetails,
                prefix: updateSettingsPrefix(formattedTargetUser),
                t,
            });

        // Accepted by ux
        case ActivityType.AssignLicenseSystemAccessToUser:
        case ActivityType.RemoveLicenseSystemAccessFromUser: {
            const messageKey = activity.activityType === ActivityType.AssignLicenseSystemAccessToUser
                ? 'assignLicenseSystemAccessToUser'
                : 'removeLicenseSystemAccessFromUser';
            const details = activity.activityDetails?.__typename === 'ActivityChangedLicenseSystemAccess' ? activity.activityDetails : null;
            const productName = mapModuleIdToName(details?.moduleId);
            const ids = getLicenseSystemIdsText(details?.licenseSystemIds) || EMPTY_VALUE;
            return translateActivityMessage(t, messageKey, activity.activityType === ActivityType.AssignLicenseSystemAccessToUser
                ? 'Assigned {{user}} access to {{product}} (license system {{ids}})'
                : 'Removed {{user}} access to {{product}} (license system {{ids}})', {
                user: formattedTargetUser,
                product: productName,
                ids,
            });
        }

        // Accepted by ux
        case ActivityType.AssignModulesToUser:
        case ActivityType.RemoveModulesFromUser: {
            const products = getModuleNamesText(activity.activityDetails) || EMPTY_VALUE;
            const messageKey = activity.activityType === ActivityType.AssignModulesToUser
                ? 'assignModulesToUser'
                : 'removeModulesFromUser';
            return translateActivityMessage(t, messageKey, activity.activityType === ActivityType.AssignModulesToUser
                ? 'Assigned seat for {{products}} to {{user}}'
                : 'Removed seat for {{products}} from {{user}}', {
                products,
                user: formattedTargetUser,
            });
        }

        // Accepted by ux
        case ActivityType.JoinOrganization:
            return translateActivityMessage(t, 'joinOrganization', 'Accepted invitation');
        // Accepted by ux
        case ActivityType.CreateOrganization:
            return translateActivityMessage(t, 'createOrganization', 'Created organization {{orgName}}', {
                orgName,
            });
        // Accepted by ux
        case ActivityType.EditOrganization:
            return formatChangedAttributeMessage({
                details: activity.activityDetails,
                prefix: updateSettingsPrefix(orgName),
                t,
            });

        // Accepted by ux
        case ActivityType.EditLicenseSystem:
            // currently just location the only one attrubute which can be changed!
            return formatChangedAttributeMessage({
                details: activity.activityDetails,
                prefix: updateSettingsPrefix(licenseSystemId),
                t,
            });

        // Accepted by ux
        case ActivityType.DownloadLicenseUpdate: {
            const updateNumber = getUpdateNumber(activity.activityDetails);
            if (updateNumber === null) {
                return translateActivityMessage(
                    t,
                    'downloadLicenseUpdateFallback',
                    'Downloaded offline license for license system {{licenseSystemId}}',
                    {
                        licenseSystemId,
                    },
                );
            }
            return translateActivityMessage(
                t,
                'downloadLicenseUpdate',
                'Downloaded offline license for license system {{licenseSystemId}} (update #{{updateNumber}})',
                {
                    licenseSystemId,
                    updateNumber,
                },
            );
        }

        // Accepted by ux
        case ActivityType.AddLicenseSystemToOrg:
        case ActivityType.RemoveLicenseSystemFromOrg:
            return translateActivityMessage(
                t,
                activity.activityType === ActivityType.AddLicenseSystemToOrg
                    ? 'addLicenseSystemToOrg'
                    : 'removeLicenseSystemFromOrg',
                activity.activityType === ActivityType.AddLicenseSystemToOrg
                    ? 'Added license system {{licenseSystemId}} to organization'
                    : 'Removed license system {{licenseSystemId}} from organization',
                {
                    licenseSystemId,
                },
            );

        case ActivityType.RequestLicensesTransfer:
            return translateActivityMessage(
                t,
                'requestLicensesTransfer',
                'Requested license transfer for license system {{licenseSystemId}}',
                {
                    licenseSystemId,
                },
            );
        case ActivityType.CompleteLicensesTransfer:
            return translateActivityMessage(
                t,
                'completeLicensesTransfer',
                'Successfully completed license transfer for license system {{licenseSystemId}}',
                {
                    licenseSystemId,
                },
            );
        case ActivityType.FailLicensesTransfer:
            return translateActivityMessage(
                t,
                'failLicensesTransfer',
                'Could not complete license transfer for license system {{licenseSystemId}}, please contact GMG Support',
                {
                    licenseSystemId,
                },
            );
        case ActivityType.RequestRepairLicenseSystem:
            return translateActivityMessage(
                t,
                'requestRepairLicenseSystem',
                'Requested repair for license system {{licenseSystemId}}',
                {
                    licenseSystemId,
                },
            );
        case ActivityType.RequestTemporaryLicenses:
            return translateActivityMessage(
                t,
                'requestTemporaryLicenses',
                'Requested temporary licenses for license system {{licenseSystemId}}',
                {
                    licenseSystemId,
                },
            );
        case ActivityType.RequestCreateLicenseSystem:
            //  We don't have a license system ID for this use case, as the system does not exist yet.
            return translateActivityMessage(t, 'requestCreateLicenseSystem', 'Requested a new license system');

        case ActivityType.AssignSitesToOrganization:
        case ActivityType.RemoveSitesFromOrganization: {
            const isAssign = activity.activityType === ActivityType.AssignSitesToOrganization;
            const headOrgName = getHeadOrgName(activity.activityDetails);

            // Same underlying event, recorded once against the head org (with the sites list) and once against each site (with the headOrg ref).
            if (headOrgName !== null) {
                return translateActivityMessage(
                    t,
                    isAssign ? 'assignedAsSiteToOrganization' : 'removedAsSiteFromOrganization',
                    isAssign
                        ? 'Assigned organization to head organization {{headOrgName}}.'
                        : 'Removed organization from head organization {{headOrgName}}.',
                    { headOrgName: addQuotationMarks(headOrgName) },
                );
            }

            const siteNames = getSiteNamesText(activity.activityDetails);
            return translateActivityMessage(
                t,
                isAssign ? 'assignSitesToOrganization' : 'removeSitesFromOrganization',
                isAssign
                    ? 'Assigned site(s): {{siteNames}}.'
                    : 'Removed site(s): {{siteNames}}.',
                { siteNames },
            );
        }
        default: {
            const exhaustiveCheck: never = activity;
            return exhaustiveCheck;
        }
    }
};
